import * as m from '@/paraglide/messages';
import type { Voice } from '@listening/api-contract/legacy-client-types';
import type { SupportedLanguage } from '@listening/api-contract/schemas';
import { useEffect, useRef, useState } from 'react';

export function useGetterEveryFrameFps<T>(fn: () => T, fps = 30) {
  const [value, setValue] = useState<T>(() => fn());
  const stableFnRef = useRef(fn);
  const frameMs = 1000 / fps;

  useEffect(() => {
    stableFnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    let rafId = 0;
    let last = 0;

    const loop = (ts: number) => {
      if (ts - last >= frameMs) {
        last = ts;
        setValue(stableFnRef.current());
      }
      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [frameMs]);

  return value;
}

// Exhaustive over the canonical language set: adding a language to
// SupportedLanguage forces an entry here (compile error otherwise).
const VOICE_LANG_LABELS: Record<SupportedLanguage, () => string> = {
  en: m.common_english,
  es: m.common_spanish,
  fr: m.onboarding_voice_french,
  hi: m.onboarding_voice_hindi,
  it: m.onboarding_voice_italian,
  ja: m.onboarding_voice_japanese,
  pt: m.onboarding_voice_portuguese,
  zh: m.onboarding_voice_chinese,
};

export function voiceLangToLangLabel(lang: Voice['lang']) {
  return (VOICE_LANG_LABELS[lang] ?? m.common_english)();
}

// Intersection-observer hook: returns a ref-setter + whether the element is on
// screen. Used as the infinite-scroll sentinel for the paginated audio lists
// (library / folder / processing) — render `<div ref={setRef} />` at the list
// bottom and call `fetchNextPage()` when `isVisible && hasNextPage`.
export const useElementOnScreen = (options?: IntersectionObserverInit) => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;

    if (ref) {
      observer = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        setIsVisible(entry.isIntersecting);
      }, options);

      observer.observe(ref);
    }

    return () => {
      if (observer && ref) {
        observer.unobserve(ref);
        observer.disconnect();
      }
    };
  }, [ref, options]);

  return [setRef, isVisible] as const;
};

// https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/adding-cloudfront-headers.html
export type Iso3166_1_alpha_2_partial = 'US' | 'CA' | 'UK' | 'EU' | 'IN' | 'PH';
